import { useEffect, useMemo, useState } from "react";
import type { FiatRateHistory } from "~/calculations/genericCalculations/convertCurrencyOnDate";

/**
 * Loads daily fiat rate history for the given currencies from the shared
 * fiat-history endpoint. Consumed by the MarketsData widget and by cashflow
 * to convert foreign-currency transactions at their transaction date.
 */
export function useFiatHistory(currencies: string[]) {
  const key = useMemo(
    () => Array.from(new Set(currencies.filter(Boolean))).sort().join(","),
    [currencies]
  );
  const [history, setHistory] = useState<FiatRateHistory | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!key) {
      setHistory(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    fetch(`/api/v1/fiatHistory?currencies=${encodeURIComponent(key)}`, {
      credentials: "same-origin",
    })
      .then((res) => (res.ok ? res.json() : Promise.reject(new Error(String(res.status)))))
      .then((data: { ok: boolean; history: FiatRateHistory }) => {
        if (!cancelled && data?.ok) setHistory(data.history);
      })
      .catch(() => {
        if (!cancelled) setHistory(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [key]);

  return { history, loading };
}

export default useFiatHistory;
